import { alpha } from "@mui/material/styles";

const buttonSizeStyles = {
  small: {
    minHeight: 32,
    paddingInline: 14,
  },
  medium: {
    minHeight: 40,
    paddingInline: 18,
  },
  large: {
    minHeight: 48,
    paddingInline: 24,
  },
};

const chipToneByColor = (theme, color) => {
  const tones = {
    primary: theme.palette.feedback.info,
    success: theme.palette.feedback.success,
    warning: theme.palette.feedback.warning,
    error: theme.palette.feedback.error,
    info: theme.palette.feedback.info,
    default: theme.palette.feedback.neutral,
  };

  return tones[color] || tones.default;
};

const alertToneBySeverity = (theme, severity) => {
  const tones = {
    success: theme.palette.feedback.success,
    warning: theme.palette.feedback.warning,
    error: theme.palette.feedback.error,
    info: theme.palette.feedback.info,
  };

  return tones[severity] || tones.info;
};

export const componentDefaults = {
  MuiAlert: {
    defaultProps: {
      variant: "standard",
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const tone = alertToneBySeverity(theme, ownerState.severity);

        return {
          alignItems: "center",
          backgroundColor: tone.surface,
          border: `1px solid ${tone.border}`,
          borderRadius: theme.shape.radii.md,
          color: tone.text,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
          padding: "10px 14px",
        };
      },
      icon: ({ ownerState, theme }) => ({
        color: alertToneBySeverity(theme, ownerState.severity).accent,
        marginRight: 10,
        padding: 0,
      }),
      message: {
        padding: 0,
      },
      action: ({ ownerState, theme }) => ({
        alignItems: "center",
        color: alertToneBySeverity(theme, ownerState.severity).accent,
        paddingBottom: 0,
        paddingTop: 0,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderRadius: theme.shape.radii.pill,
        fontSize: theme.typography.button.fontSize || theme.typography.body2.fontSize,
        fontWeight: theme.typography.button.fontWeight,
        lineHeight: 1.25,
        minWidth: 0,
        textTransform: "none",
        transition:
          "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease",
        ...buttonSizeStyles[ownerState.size || "medium"],
        "&.Mui-disabled": {
          backgroundColor:
            ownerState.variant === "contained"
              ? theme.palette.action.disabledBackground
              : "transparent",
          borderColor:
            ownerState.variant === "outlined"
              ? theme.palette.border.muted
              : "transparent",
          color: theme.palette.text.disabled,
        },
      }),
      containedPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
        "&:focus-visible": {
          boxShadow: `0 0 0 3px ${theme.palette.focusRing}`,
        },
      }),
      outlinedPrimary: ({ theme }) => ({
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderColor: theme.palette.primary.dark,
        },
        "&:focus-visible": {
          boxShadow: `0 0 0 3px ${theme.palette.focusRing}`,
        },
      }),
      textPrimary: ({ theme }) => ({
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
        },
        "&:focus-visible": {
          boxShadow: `0 0 0 3px ${theme.palette.focusRing}`,
        },
      }),
      startIcon: {
        marginLeft: 0,
      },
      endIcon: {
        marginRight: 0,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const tone = chipToneByColor(theme, ownerState.color);

        return {
          backgroundColor: tone.surface,
          border: `1px solid ${tone.border}`,
          borderRadius: theme.shape.radii.sm,
          color: tone.text,
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 700,
          height: ownerState.size === "small" ? 24 : 28,
          lineHeight: 1,
        };
      },
      label: ({ ownerState }) => ({
        paddingInline: ownerState.size === "small" ? 8 : 10,
      }),
      icon: ({ ownerState, theme }) => ({
        color: chipToneByColor(theme, ownerState.color).accent,
        marginLeft: 8,
      }),
      deleteIcon: ({ ownerState, theme }) => ({
        color: chipToneByColor(theme, ownerState.color).accent,
        "&:hover": {
          color: chipToneByColor(theme, ownerState.color).text,
        },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: "18px",
        marginLeft: 0,
        marginTop: 4,
        "&.Mui-error": {
          color: theme.palette.error.main,
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: "20px",
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
        "&.Mui-error": {
          color: theme.palette.error.main,
        },
        "&.Mui-disabled": {
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "hover",
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        fontSize: theme.typography.body2.fontSize,
        minHeight: 40,
        paddingBlock: 8,
        paddingInline: 14,
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
          },
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        backgroundColor: theme.palette.surface.default,
        borderRadius: theme.shape.radii.sm,
        color: theme.palette.text.primary,
        fontSize: theme.typography.body2.fontSize,
        minHeight: ownerState.multiline ? undefined : 48,
        transition: "box-shadow 160ms ease",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.border.default,
          transition: "border-color 160ms ease",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.border.strong,
        },
        "&.Mui-focused": {
          boxShadow: `0 0 0 1px ${theme.palette.focusRing}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.border.focus,
          borderWidth: 1,
        },
        "&.Mui-error": {
          boxShadow: `0 0 0 1px ${theme.palette.errorRing}`,
        },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.border.error,
        },
        "&.Mui-disabled": {
          backgroundColor: theme.palette.surface.muted,
          color: theme.palette.text.disabled,
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.border.muted,
        },
      }),
      input: ({ ownerState, theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        lineHeight: "20px",
        minWidth: 0,
        overflow: ownerState.multiline ? undefined : "hidden",
        padding: ownerState.size === "small" ? "9px 12px" : "13px 14px",
        textOverflow: ownerState.multiline ? undefined : "ellipsis",
        whiteSpace: ownerState.multiline ? undefined : "nowrap",
        "&::placeholder": {
          color: theme.palette.text.disabled,
          opacity: 1,
        },
      }),
      multiline: {
        padding: "12px 14px",
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: "outlined",
    },
    styleOverrides: {
      select: ({ theme }) => ({
        alignItems: "center",
        display: "flex",
        minWidth: 0,
        minHeight: "20px",
        overflow: "hidden",
        paddingBlock: 13,
        paddingLeft: 14,
        paddingRight: 40,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "&:focus": {
          backgroundColor: "transparent",
        },
        "&.Mui-disabled": {
          color: theme.palette.text.disabled,
        },
      }),
      icon: ({ theme }) => ({
        color: theme.palette.text.secondary,
        right: 12,
      }),
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: "outlined",
    },
  },
};
